(() => {
  'use strict';

  const SELECTORS = {
    drawer: '[data-cart-drawer]',
    drawerContent: '[data-cart-drawer-content]',
    drawerOpen: '[data-cart-drawer-open]',
    drawerClose: '[data-cart-drawer-close]',
    cartCount: '[data-cart-count]',
    cartStatus: '[data-cart-status]',
    productForm: 'form[data-product-form], form[action$="/cart/add"], form[action$="/cart/add.js"]',
    menu: '[data-mobile-menu]',
    menuOpen: '[data-menu-open]',
    menuClose: '[data-menu-close]'
  };

  const routes = () => {
    const configured = window.Selntz && window.Selntz.routes ? window.Selntz.routes : {};
    const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
    return {
      add: configured.cartAdd || `${root}cart/add.js`,
      change: configured.cartChange || `${root}cart/change.js`,
      cart: configured.cart || `${root}cart.js`,
      cartUrl: configured.cartUrl || `${root}cart`
    };
  };

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  let lastFocusedElement = null;
  let pendingQuantityRequest = null;

  const getDrawer = () => document.querySelector(SELECTORS.drawer);
  const getMenu = () => document.querySelector(SELECTORS.menu);

  const lockPage = () => {
    document.documentElement.classList.add('overlay-open');
    document.body.classList.add('overlay-open');
  };

  const unlockPage = () => {
    if (!document.querySelector('.is-open[data-cart-drawer], .is-open[data-mobile-menu]')) {
      document.documentElement.classList.remove('overlay-open');
      document.body.classList.remove('overlay-open');
    }
  };

  const focusFirst = (container) => {
    const focusable = container.querySelectorAll(focusableSelector);
    if (focusable.length) focusable[0].focus();
  };

  const trapFocus = (event, container) => {
    if (event.key !== 'Tab') return;
    const focusable = Array.from(container.querySelectorAll(focusableSelector)).filter(
      (element) => !element.hasAttribute('hidden') && element.offsetParent !== null
    );
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const openOverlay = (overlay, trigger) => {
    if (!overlay) return;
    lastFocusedElement = trigger || document.activeElement;
    overlay.hidden = false;
    overlay.setAttribute('aria-hidden', 'false');
    trigger?.setAttribute('aria-expanded', 'true');
    lockPage();
    window.requestAnimationFrame(() => {
      overlay.classList.add('is-open');
      const panel = overlay.querySelector('[role="dialog"]');
      if (panel) focusFirst(panel);
    });
  };

  const closeOverlay = (overlay, restoreFocus = true) => {
    if (!overlay || overlay.hidden) return;
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    const controlledId = overlay.id;
    if (controlledId) {
      document.querySelectorAll(`[aria-controls="${controlledId}"]`).forEach((button) => {
        button.setAttribute('aria-expanded', 'false');
      });
    }
    window.setTimeout(() => {
      if (!overlay.classList.contains('is-open')) overlay.hidden = true;
      unlockPage();
    }, 360);
    if (restoreFocus && lastFocusedElement instanceof HTMLElement) lastFocusedElement.focus();
  };

  const openDrawer = (trigger) => {
    closeOverlay(getMenu(), false);
    openOverlay(getDrawer(), trigger);
  };

  const closeDrawer = () => closeOverlay(getDrawer());

  const openMenu = (trigger) => {
    closeOverlay(getDrawer(), false);
    openOverlay(getMenu(), trigger);
  };

  const closeMenu = () => closeOverlay(getMenu());

  const updateCartCount = (count) => {
    const safeCount = Number.isFinite(Number(count)) ? Number(count) : 0;
    document.querySelectorAll(SELECTORS.cartCount).forEach((node) => {
      node.textContent = String(safeCount);
      node.hidden = safeCount === 0;
    });
    document.querySelectorAll(SELECTORS.drawerOpen).forEach((button) => {
      button.setAttribute('aria-label', `Open cart, ${safeCount} ${safeCount === 1 ? 'item' : 'items'}`);
    });
  };

  const setCartStatus = (message) => {
    const status = document.querySelector(SELECTORS.cartStatus);
    if (status) status.textContent = message;
  };

  const getCart = async () => {
    const response = await fetch(routes().cart, {
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!response.ok) throw new Error('Cart could not be loaded.');
    return response.json();
  };

  const replaceDrawerContent = (html) => {
    if (!html) return false;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const incoming = parsed.querySelector(SELECTORS.drawerContent);
    const current = document.querySelector(SELECTORS.drawerContent);
    if (!incoming || !current) return false;
    current.innerHTML = incoming.innerHTML;
    return true;
  };

  const fetchDrawerSection = async () => {
    const drawer = getDrawer();
    const sectionId = drawer?.dataset.cartSectionId;
    if (!sectionId) return false;
    const url = new URL(window.location.href);
    url.searchParams.set('sections', sectionId);
    const response = await fetch(url.toString(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!response.ok) throw new Error('Cart display could not be refreshed.');
    const sections = await response.json();
    return replaceDrawerContent(sections[sectionId]);
  };

  const updateFromResponse = async (payload) => {
    const drawer = getDrawer();
    const sectionId = drawer?.dataset.cartSectionId;
    let replaced = false;
    if (payload && payload.sections && sectionId) {
      replaced = replaceDrawerContent(payload.sections[sectionId]);
    }
    if (!replaced) await fetchDrawerSection();

    const cart = payload && typeof payload.item_count === 'number' ? payload : await getCart();
    updateCartCount(cart.item_count);
    document.dispatchEvent(new CustomEvent('selntz:cart-updated', { detail: { cart } }));
    return cart;
  };

  const requestOptions = (body, json = false) => ({
    method: 'POST',
    headers: json
      ? { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      : { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body
  });

  const parseResponse = async (response) => {
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.description || payload.message || 'Something went wrong. Please try again.');
    }
    return payload;
  };

  const addProduct = async (form, submitter) => {
    const variantInput = form.querySelector('[name="id"]');
    if (!variantInput || !variantInput.value) return;

    const drawer = getDrawer();
    const formData = new FormData(form);
    const sectionId = drawer?.dataset.cartSectionId;
    if (sectionId) {
      formData.set('sections', sectionId);
      formData.set('sections_url', `${window.location.pathname}${window.location.search}`);
    }

    const button = submitter || form.querySelector('[type="submit"]');
    const originalLabel = button?.querySelector('[data-add-to-cart-text]')?.textContent;
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.classList.add('is-loading');
    }
    setCartStatus('Adding item to cart.');

    try {
      const response = await fetch(routes().add, requestOptions(formData));
      const payload = await parseResponse(response);
      await updateFromResponse(payload);
      openDrawer(button);
      setCartStatus('Item added to cart.');
    } catch (error) {
      setCartStatus(error.message);
      document.dispatchEvent(new CustomEvent('selntz:cart-error', { detail: { message: error.message, form } }));
      const errorNode = form.querySelector('[data-product-form-error]');
      if (errorNode) {
        errorNode.textContent = error.message;
        errorNode.hidden = false;
      }
    } finally {
      if (button) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.classList.remove('is-loading');
        const label = button.querySelector('[data-add-to-cart-text]');
        if (label && originalLabel) label.textContent = originalLabel;
      }
    }
  };

  const changeQuantity = async (lineKey, quantity, control) => {
    if (!lineKey || pendingQuantityRequest) return;
    const drawer = getDrawer();
    const sectionId = drawer?.dataset.cartSectionId;
    const body = {
      id: lineKey,
      quantity: Math.max(0, Number(quantity) || 0),
      sections: sectionId ? [sectionId] : [],
      sections_url: `${window.location.pathname}${window.location.search}`
    };
    control?.setAttribute('aria-busy', 'true');
    drawer?.classList.add('is-updating');
    setCartStatus('Updating cart.');

    pendingQuantityRequest = fetch(routes().change, requestOptions(JSON.stringify(body), true))
      .then(parseResponse)
      .then(updateFromResponse)
      .then(() => setCartStatus('Cart updated.'))
      .catch((error) => setCartStatus(error.message))
      .finally(() => {
        pendingQuantityRequest = null;
        control?.removeAttribute('aria-busy');
        drawer?.classList.remove('is-updating');
      });
    await pendingQuantityRequest;
  };

  document.addEventListener('submit', (event) => {
    const form = event.target.closest(SELECTORS.productForm);
    if (!form || form.dataset.ajaxCart === 'false') return;
    const submitter = event.submitter;
    if (submitter?.name === 'checkout') return;
    event.preventDefault();
    addProduct(form, submitter);
  });

  document.addEventListener('click', (event) => {
    const drawerOpen = event.target.closest(SELECTORS.drawerOpen);
    if (drawerOpen) {
      event.preventDefault();
      openDrawer(drawerOpen);
      return;
    }
    if (event.target.closest(SELECTORS.drawerClose)) {
      closeDrawer();
      return;
    }
    const menuOpen = event.target.closest(SELECTORS.menuOpen);
    if (menuOpen) {
      openMenu(menuOpen);
      return;
    }
    if (event.target.closest(SELECTORS.menuClose)) {
      closeMenu();
      return;
    }

    const quantityButton = event.target.closest('[data-cart-quantity]');
    if (quantityButton) {
      changeQuantity(quantityButton.dataset.lineKey, quantityButton.dataset.cartQuantity, quantityButton);
      return;
    }
    const removeLink = event.target.closest('[data-cart-remove]');
    if (removeLink) {
      event.preventDefault();
      changeQuantity(removeLink.dataset.lineKey, 0, removeLink);
    }
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-cart-quantity-input]');
    if (input) changeQuantity(input.dataset.lineKey, input.value, input);
  });

  document.addEventListener('keydown', (event) => {
    const drawer = getDrawer();
    const menu = getMenu();
    if (event.key === 'Escape') {
      if (drawer?.classList.contains('is-open')) closeDrawer();
      else if (menu?.classList.contains('is-open')) closeMenu();
      return;
    }
    if (drawer?.classList.contains('is-open')) trapFocus(event, drawer);
    else if (menu?.classList.contains('is-open')) trapFocus(event, menu);
  });

  document.addEventListener('selntz:cart-updated', (event) => {
    if (event.detail?.cart) updateCartCount(event.detail.cart.item_count);
  });

  const updateHeader = () => {
    document.querySelector('[data-site-header]')?.classList.toggle('is-scrolled', window.scrollY > 12);
  };
  updateHeader();
  window.addEventListener('scroll', updateHeader, { passive: true });
})();
